// Cajón de creación de una carga.
//
// Lo que se envía NO es una carga: es una solicitud. La entidad de D365 no admite
// escritura, así que un robot la teclea minutos después y Fabric la enseña unos
// quince más tarde. De ahí dos decisiones de pantalla:
//
//   1. El botón dice «solicitar», y al terminar se explica la espera en vez de
//      dejar al usuario mirando una lista donde no ha cambiado nada.
//   2. El disponible de cada línea ya descuenta lo pedido y todavía sin carga.
//      Sin eso, dos personas —o la misma dos veces— piden lo mismo.
//
// El cromado es el del cajón de documentos a propósito: en esta misma pantalla el
// usuario ya abre un panel lateral, y la consistencia gana a la novedad.
import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader } from '@/components/common/Loader'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { pedidosVentasApi } from '@/services/api'
import type {
  CargaCreateResult,
  CargaLinea,
  CargaValidacionError,
  PedidoVentaLinea,
  SolicitudCarga,
  TipoEnvio,
} from '@/types'
import {
  borradorListo,
  disponiblePorLinea,
  totalBorrador,
  validarBorrador,
  type BorradorCarga,
  type DisponibleLinea,
} from '@/utils/cargasCreacion'
import { fmtQty } from './formato'

interface Props {
  dark: boolean
  empresa: string
  numPedido: string
  lineas: PedidoVentaLinea[]
  cargas: CargaLinea[]
  solicitudes: SolicitudCarga[]
  onClose: () => void
  /** La solicitud ya está cursada: el padre refresca y avisa. */
  onEnviada: (resultado: CargaCreateResult) => void
}

/** Cabecera del formulario. Los cuatro primeros campos son obligatorios; el tipo
 *  de envío siempre tiene valor porque arranca en general. */
interface Cabecera {
  fecha: string
  referenciaCliente: string
  cargaCliente: string
  noPaletizada: boolean
  tipoEnvio: TipoEnvio
}

const HOY = () => new Date().toISOString().slice(0, 10)

// ── Componente ────────────────────────────────────────────────────────────────

export function NuevaCargaDrawer({
  dark, empresa, numPedido, lineas, cargas, solicitudes, onClose, onEnviada,
}: Props) {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const [open, setOpen] = useState(false)
  const [cab, setCab] = useState<Cabecera>({
    fecha: HOY(), referenciaCliente: '', cargaCliente: '', noPaletizada: false,
    // General de salida: es lo que sale casi siempre, y equivocarse hacia aéreo
    // sale más caro que al revés.
    tipoEnvio: 'general',
  })
  const [borrador, setBorrador] = useState<BorradorCarga>(new Map())
  // Los huecos de la cabecera no se marcan en rojo nada más abrir: eso sería
  // regañar a alguien que aún no ha tenido ocasión de escribir.
  const [intentado, setIntentado] = useState(false)
  const [enviada, setEnviada] = useState<CargaCreateResult | null>(null)
  const [errorServidor, setErrorServidor] = useState<ErrorServidor | null>(null)

  const disponibles = useMemo(
    () => disponiblePorLinea(lineas, cargas, solicitudes),
    [lineas, cargas, solicitudes],
  )
  const conDisponible = useMemo(
    () => disponibles.filter(d => d.disponible > 0).length,
    [disponibles],
  )

  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true))
    return () => cancelAnimationFrame(id)
  }, [])

  function handleClose() {
    setOpen(false)
    setTimeout(onClose, 340)
  }

  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose() }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const enviar = useMutation({
    mutationFn: () => pedidosVentasApi.crearCarga(numPedido, {
      empresa,
      fecha: cab.fecha,
      referenciaCliente: cab.referenciaCliente.trim(),
      cargaCliente: cab.cargaCliente.trim(),
      noPaletizada: cab.noPaletizada,
      tipoEnvio: cab.tipoEnvio,
      lineas: [...borrador.entries()]
        .filter(([, cantidad]) => cantidad > 0)
        .map(([transid, cantidad]) => {
          const linea = disponibles.find(d => d.transid === transid)
          return {
            inventtransid: transid,
            numLinea: linea?.numLinea ?? null,
            articulo: linea?.articulo ?? '',
            color: linea?.color,
            estilo: linea?.estilo,
            cantidad,
          }
        }),
    }),
    onSuccess: (resp) => {
      setEnviada(resp.data)
      onEnviada(resp.data)
    },
    onError: (e) => setErrorServidor(leerError(e)),
  })

  const errores = validarBorrador(borrador, disponibles)
  const total = totalBorrador(borrador)
  const cabeceraCompleta = !!(cab.fecha && cab.referenciaCliente.trim() && cab.cargaCliente.trim())
  const puedeEnviar = cabeceraCompleta && borradorListo(borrador, disponibles) && !enviar.isPending

  function marcar(linea: DisponibleLinea, activa: boolean) {
    setErrorServidor(null)
    setBorrador(prev => {
      const next = new Map(prev)
      // Marcar rellena con todo lo disponible: es lo que se pide casi siempre, y
      // recortar es más fácil que teclear la cifra entera.
      if (activa) next.set(linea.transid, linea.disponible)
      else next.delete(linea.transid)
      return next
    })
  }

  function cantidad(linea: DisponibleLinea, valor: number) {
    setErrorServidor(null)
    setBorrador(prev => new Map(prev).set(linea.transid, valor))
  }

  function todas() {
    setErrorServidor(null)
    setBorrador(new Map(
      disponibles.filter(d => d.disponible > 0).map(d => [d.transid, d.disponible]),
    ))
  }

  function ninguna() {
    setErrorServidor(null)
    setBorrador(new Map())
  }

  // ── Cromado ────────────────────────────────────────────────────────────────
  const bg       = dark ? 'bg-[#0b0f16]' : 'bg-white'
  const border   = dark ? 'border-white/[0.07]' : 'border-slate-200'
  const mainTxt  = dark ? 'text-slate-100' : 'text-slate-900'
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const dimTxt   = dark ? 'text-slate-600' : 'text-slate-400'

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      <div
        onClick={handleClose}
        className="absolute inset-0 cursor-pointer"
        style={{
          backgroundColor: open ? 'rgba(0,0,0,0.52)' : 'rgba(0,0,0,0)',
          backdropFilter: open ? 'blur(3px)' : 'blur(0px)',
          transition: 'background-color 0.34s ease, backdrop-filter 0.34s ease',
        }}
      />

      <div
        className={`relative flex flex-col h-full shadow-2xl ${bg} border-l ${border}`}
        style={{
          width: isMobile ? '100vw' : 'min(92vw, 920px)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.34s cubic-bezier(0.32, 0, 0.1, 1)',
          boxShadow: dark
            ? '-32px 0 80px rgba(0,0,0,0.6), -1px 0 0 rgba(255,255,255,0.05)'
            : '-32px 0 80px rgba(0,0,0,0.12), -1px 0 0 rgba(0,0,0,0.06)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label={`${t('salesOrders.nuevaCarga.title')} — ${numPedido}`}
      >
        {/* ── Cabecera ──────────────────────────────────────────────────────── */}
        <div className={`flex items-center gap-4 px-6 py-4 border-b ${border} shrink-0`}>
          <div className={`p-2 rounded-lg ${dark ? 'bg-white/5' : 'bg-slate-100'}`}>
            <svg className={`w-4 h-4 ${mutedTxt}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9h13v9H3zM16 12h3.5l1.5 3v3h-5" />
              <circle cx="7" cy="18" r="1.6" />
              <circle cx="18" cy="18" r="1.6" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className={`text-[13px] font-semibold ${mainTxt}`}>
              {t('salesOrders.nuevaCarga.title')}
            </h2>
            <p className={`text-[11px] mt-0.5 flex items-center gap-2 flex-wrap ${mutedTxt}`}>
              <span className="font-mono font-semibold">{numPedido}</span>
              <span className={dimTxt}>·</span>
              <span className="font-mono">{empresa.toUpperCase()}</span>
            </p>
          </div>
          <button
            onClick={handleClose}
            aria-label={t('salesOrders.nuevaCarga.cerrar')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] transition-colors cursor-pointer ${
              dark ? 'text-slate-500 hover:text-slate-300 hover:bg-white/6'
                   : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            }`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            <span className={`text-[10px] font-mono hidden sm:inline ${dimTxt}`}>ESC</span>
          </button>
        </div>

        {enviada ? (
          <Confirmacion dark={dark} resultado={enviada} onClose={handleClose} />
        ) : (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
              <FormularioCabecera
                dark={dark}
                valor={cab}
                intentado={intentado}
                onCambio={(patch) => { setErrorServidor(null); setCab(c => ({ ...c, ...patch })) }}
              />

              <TablaLineas
                dark={dark}
                disponibles={disponibles}
                conDisponible={conDisponible}
                borrador={borrador}
                onMarcar={marcar}
                onCantidad={cantidad}
                onTodas={todas}
                onNinguna={ninguna}
              />

              {errorServidor && (
                <AvisoError dark={dark} error={errorServidor} disponibles={disponibles} />
              )}
            </div>

            {/* ── Barra de totales ──────────────────────────────────────────── */}
            <div className={`flex items-center gap-3 flex-wrap px-6 py-3.5 border-t ${border} shrink-0`}>
              <p className={`text-[11px] flex-1 min-w-0 ${mutedTxt}`}>
                {total.lineas > 0 ? (
                  <>
                    <span className={`font-semibold ${mainTxt}`}>
                      {t('salesOrders.nuevaCarga.resumen', { count: total.lineas })}
                    </span>
                    <span className={dimTxt}> · </span>
                    <span className="tabular-nums font-semibold">{fmtQty(total.unidades)}</span>{' '}
                    {t('salesOrders.picking.units')}
                  </>
                ) : (
                  t('salesOrders.nuevaCarga.nadaSeleccionado')
                )}
                {errores.length > 0 && (
                  <span className={`ml-2 font-semibold ${dark ? 'text-red-400' : 'text-red-600'}`}>
                    · {t('salesOrders.nuevaCarga.conErrores', { count: errores.length })}
                  </span>
                )}
              </p>

              {enviar.isPending && <Loader size={7} />}

              <button
                type="button"
                onClick={handleClose}
                className={`px-3 py-2 rounded-lg text-[12px] font-medium transition-colors cursor-pointer ${
                  dark ? 'text-slate-300 hover:bg-white/6' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {t('salesOrders.nuevaCarga.cancelar')}
              </button>
              <button
                type="button"
                onClick={() => { setIntentado(true); if (puedeEnviar) enviar.mutate() }}
                disabled={enviar.isPending}
                title={puedeEnviar ? undefined : t('salesOrders.nuevaCarga.faltaAlgo')}
                className={`px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors shadow-sm ${
                  puedeEnviar
                    ? 'bg-sky-600 text-white hover:bg-sky-500 cursor-pointer'
                    : `cursor-not-allowed ${dark ? 'bg-white/8 text-slate-500' : 'bg-slate-200 text-slate-400'}`
                }`}
              >
                {enviar.isPending
                  ? t('salesOrders.nuevaCarga.enviando')
                  : t('salesOrders.nuevaCarga.enviar')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── Cabecera de la carga ──────────────────────────────────────────────────────

function FormularioCabecera({
  dark, valor, intentado, onCambio,
}: {
  dark: boolean
  valor: Cabecera
  intentado: boolean
  onCambio: (patch: Partial<Cabecera>) => void
}) {
  const { t } = useTranslation()
  const label = `text-[9px] font-bold uppercase tracking-wider ${dark ? 'text-slate-500' : 'text-slate-400'}`
  const base = `w-full mt-1 px-2.5 py-2 rounded-md text-[12px] border outline-none transition-colors ${
    dark
      ? 'bg-white/[0.04] text-slate-200 placeholder:text-slate-600 focus:border-sky-500/60'
      : 'bg-white text-slate-800 placeholder:text-slate-300 focus:border-sky-400'
  }`
  const falta = (v: string) => intentado && !v.trim()
  const borde = (v: string) => falta(v)
    ? (dark ? 'border-amber-500/60' : 'border-amber-400')
    : (dark ? 'border-white/10' : 'border-slate-200')

  return (
    <Seccion titulo={t('salesOrders.nuevaCarga.datos')} dark={dark}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className={label} htmlFor="nc-fecha">
            {t('salesOrders.nuevaCarga.fecha')}
          </label>
          <input
            id="nc-fecha"
            type="date"
            value={valor.fecha}
            onChange={e => onCambio({ fecha: e.target.value })}
            className={`${base} ${borde(valor.fecha)}`}
          />
        </div>
        <div>
          <label className={label} htmlFor="nc-ref">
            {t('salesOrders.nuevaCarga.referenciaCliente')}
          </label>
          <input
            id="nc-ref"
            type="text"
            value={valor.referenciaCliente}
            onChange={e => onCambio({ referenciaCliente: e.target.value })}
            placeholder={t('salesOrders.nuevaCarga.referenciaClientePh')}
            className={`${base} ${borde(valor.referenciaCliente)}`}
          />
        </div>
        <div>
          <label className={label} htmlFor="nc-carga-cliente">
            {t('salesOrders.nuevaCarga.cargaCliente')}
          </label>
          <input
            id="nc-carga-cliente"
            type="text"
            value={valor.cargaCliente}
            onChange={e => onCambio({ cargaCliente: e.target.value })}
            placeholder={t('salesOrders.nuevaCarga.cargaClientePh')}
            className={`${base} ${borde(valor.cargaCliente)}`}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-stretch gap-2">
        <TipoEnvioSelector dark={dark} valor={valor.tipoEnvio} onCambio={onCambio} />

      {/* Interruptor y no casilla: es una decisión de la carga, no un extra. */}
      <button
        type="button"
        role="switch"
        aria-checked={valor.noPaletizada}
        onClick={() => onCambio({ noPaletizada: !valor.noPaletizada })}
        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left transition-colors cursor-pointer ${
          valor.noPaletizada
            ? (dark ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-indigo-50 border-indigo-200')
            : (dark ? 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06]'
                    : 'bg-slate-50 border-slate-200 hover:bg-slate-100')
        }`}
      >
        <span className={`relative w-8 h-[18px] rounded-full transition-colors shrink-0 ${
          valor.noPaletizada ? 'bg-indigo-500' : (dark ? 'bg-slate-600' : 'bg-slate-300')
        }`}>
          <span
            className="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white transition-all duration-200"
            style={{ left: valor.noPaletizada ? '16px' : '2px' }}
          />
        </span>
        <span>
          <span className={`block text-[12px] font-medium ${dark ? 'text-slate-200' : 'text-slate-800'}`}>
            {t('salesOrders.picking.noPaletizada')}
          </span>
          <span className={`block text-[10px] ${dark ? 'text-slate-500' : 'text-slate-500'}`}>
            {t('salesOrders.nuevaCarga.noPaletizadaNota')}
          </span>
        </span>
      </button>
      </div>
    </Seccion>
  )
}

/** General o aéreo: es lo único que decide la plantilla de carga en D365.
 *
 *  Se pinta como dos pestañas y no como un desplegable porque son dos opciones y
 *  la elegida tiene que verse sin abrir nada — es el campo con más consecuencias
 *  del formulario y el que más fácil es dejarse por defecto sin querer.
 *
 *  El literal de la plantilla NO vive aquí: viaja `general` o `aereo` y el backend
 *  traduce. Cambiar un nombre de plantilla no puede obligar a desplegar la web. */
function TipoEnvioSelector({
  dark, valor, onCambio,
}: {
  dark: boolean
  valor: TipoEnvio
  onCambio: (patch: Partial<Cabecera>) => void
}) {
  const { t } = useTranslation()
  const opciones: { clave: TipoEnvio; icono: string }[] = [
    { clave: 'general', icono: 'M3 9h13v9H3zM16 12h3.5l1.5 3v3h-5' },
    { clave: 'aereo', icono: 'M10.2 15.5L3 13l1.2-1.6 5 .9 4-4.6-6.4-3.6L8.4 3l8.9 2.9 2.4-2.7a1.7 1.7 0 012.4 2.3l-2.7 2.5L22 17l-1.3 1.1-3.9-5.6-4.6 4.1.9 5-1.6 1.2z' },
  ]

  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${
      dark ? 'bg-white/[0.03] border-white/10' : 'bg-slate-50 border-slate-200'
    }`}>
      <span className={`text-[9px] font-bold uppercase tracking-wider ${
        dark ? 'text-slate-500' : 'text-slate-400'}`}>
        {t('salesOrders.nuevaCarga.tipoEnvio')}
      </span>
      <div role="radiogroup" className={`flex rounded-md overflow-hidden border ${
        dark ? 'border-white/10' : 'border-slate-200'
      }`}>
        {opciones.map(({ clave, icono }) => {
          const activa = valor === clave
          return (
            <button
              key={clave}
              type="button"
              role="radio"
              aria-checked={activa}
              onClick={() => onCambio({ tipoEnvio: clave })}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold transition-colors cursor-pointer ${
                activa
                  ? (dark ? 'bg-sky-500/20 text-sky-200' : 'bg-sky-100 text-sky-800')
                  : (dark ? 'text-slate-400 hover:bg-white/6' : 'text-slate-500 hover:bg-slate-100')
              }`}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d={icono} />
              </svg>
              {t(`salesOrders.nuevaCarga.tipoEnvio_${clave}`)}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Líneas del pedido ─────────────────────────────────────────────────────────

function TablaLineas({
  dark, disponibles, conDisponible, borrador, onMarcar, onCantidad, onTodas, onNinguna,
}: {
  dark: boolean
  disponibles: DisponibleLinea[]
  conDisponible: number
  borrador: BorradorCarga
  onMarcar: (linea: DisponibleLinea, activa: boolean) => void
  onCantidad: (linea: DisponibleLinea, valor: number) => void
  onTodas: () => void
  onNinguna: () => void
}) {
  const { t } = useTranslation()
  const dimTxt = dark ? 'text-slate-600' : 'text-slate-400'
  const th = `px-2 py-1.5 text-left text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${
    dark ? 'text-slate-500' : 'text-slate-400'}`
  const enlace = `text-[10px] font-semibold transition-colors cursor-pointer ${
    dark ? 'text-sky-400 hover:text-sky-300' : 'text-sky-600 hover:text-sky-700'}`

  return (
    <Seccion
      titulo={t('salesOrders.nuevaCarga.articulos')}
      nota={t('salesOrders.nuevaCarga.lineasResumen', {
        total: disponibles.length, libres: conDisponible,
      })}
      dark={dark}
      acciones={
        conDisponible > 0 && (
          <span className="flex items-center gap-3">
            <button type="button" onClick={onTodas} className={enlace}>
              {t('salesOrders.nuevaCarga.todas')}
            </button>
            <button type="button" onClick={onNinguna} className={enlace}>
              {t('salesOrders.nuevaCarga.ninguna')}
            </button>
          </span>
        )
      }
    >
      {disponibles.length === 0 ? (
        <p className={`text-[12px] ${dimTxt}`}>{t('salesOrders.nuevaCarga.sinLineas')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] table-fixed border-collapse text-left">
            <colgroup>
              <col className="w-[34px]" />
              <col className="w-[40px]" />
              <col />
              <col className="w-[86px]" />
              <col className="w-[86px]" />
              <col className="w-[86px]" />
              <col className="w-[120px]" />
            </colgroup>
            <thead>
              <tr>
                <th className={th} />
                <th className={th}>{t('salesOrders.line.lineNum')}</th>
                <th className={th}>{t('salesOrders.line.article')}</th>
                <th className={`${th} text-right`}>{t('salesOrders.nuevaCarga.pedida')}</th>
                <th className={`${th} text-right`}>{t('salesOrders.nuevaCarga.lanzado')}</th>
                <th className={`${th} text-right`}>{t('salesOrders.nuevaCarga.disponible')}</th>
                <th className={`${th} text-right`}>{t('salesOrders.nuevaCarga.cantidad')}</th>
              </tr>
            </thead>
            <tbody>
              {disponibles.map(linea => (
                <FilaLinea
                  key={linea.transid}
                  dark={dark}
                  linea={linea}
                  cantidad={borrador.get(linea.transid)}
                  onMarcar={onMarcar}
                  onCantidad={onCantidad}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Seccion>
  )
}

function FilaLinea({
  dark, linea, cantidad, onMarcar, onCantidad,
}: {
  dark: boolean
  linea: DisponibleLinea
  cantidad?: number
  onMarcar: (linea: DisponibleLinea, activa: boolean) => void
  onCantidad: (linea: DisponibleLinea, valor: number) => void
}) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const agotada = linea.disponible <= 0
  const marcada = cantidad !== undefined
  const excede = marcada && cantidad! > linea.disponible + 1e-3
  const invalida = marcada && (!Number.isFinite(cantidad!) || cantidad! <= 0)

  const divider  = dark ? 'border-white/8' : 'border-slate-200'
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const td = `px-2 py-1.5 text-[11px] whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-700'}`

  const motivo = agotada
    ? (linea.reservado > 0
        ? t('salesOrders.nuevaCarga.agotadaPorSolicitud', { reservado: fmtQty(linea.reservado) })
        : t('salesOrders.nuevaCarga.agotada'))
    : undefined

  return (
    <tr
      className={`border-t ${divider} transition-colors ${
        agotada ? 'opacity-45' : marcada ? (dark ? 'bg-sky-500/[0.07]' : 'bg-sky-50/70') : ''
      }`}
      title={motivo}
    >
      <td className="px-2 py-1.5">
        <input
          type="checkbox"
          checked={marcada}
          disabled={agotada}
          onChange={e => onMarcar(linea, e.target.checked)}
          aria-label={t('salesOrders.nuevaCarga.incluir', { articulo: linea.articulo })}
          className={`w-3.5 h-3.5 rounded accent-sky-600 ${
            agotada ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        />
      </td>
      <td className={`${td} tabular-nums ${mutedTxt}`}>{linea.numLinea ?? '—'}</td>
      <td className={td}>
        <div className="min-w-0">
          <div className="font-semibold text-[11px] truncate" title={fmtArt(linea.articulo)}>
            {fmtArt(linea.articulo)}
            {(linea.color || linea.estilo) && (
              <span className={`ml-1.5 font-normal ${mutedTxt}`}>
                {[linea.color, linea.estilo].filter(Boolean).join(' / ')}
              </span>
            )}
          </div>
          {linea.descripcion && (
            <div className={`text-[9px] truncate ${mutedTxt}`} title={linea.descripcion}>
              {linea.descripcion}
            </div>
          )}
        </div>
      </td>
      <td className={`${td} text-right tabular-nums ${mutedTxt}`}>{fmtQty(linea.pedida)}</td>
      <td className={`${td} text-right tabular-nums ${mutedTxt}`}>
        {fmtQty(linea.lanzado)}
        {linea.reservado > 0 && (
          <span
            className={`ml-1 ${dark ? 'text-amber-400' : 'text-amber-600'}`}
            title={t('salesOrders.nuevaCarga.reservadoTooltip', {
              reservado: fmtQty(linea.reservado),
            })}
          >
            +{fmtQty(linea.reservado)}
          </span>
        )}
      </td>
      <td className={`${td} text-right tabular-nums font-semibold ${
        agotada ? mutedTxt : (dark ? 'text-emerald-400' : 'text-emerald-600')
      }`}>
        {fmtQty(linea.disponible)}
      </td>
      <td className="px-2 py-1.5">
        <div className="flex items-center justify-end gap-1">
          <input
            type="number"
            min={0}
            max={linea.disponible}
            step="any"
            disabled={agotada}
            value={cantidad ?? ''}
            onChange={e => {
              const v = e.target.value
              if (v === '') onMarcar(linea, false)
              else onCantidad(linea, Number(v))
            }}
            aria-label={t('salesOrders.nuevaCarga.cantidad')}
            className={`w-[76px] px-2 py-1 rounded-md text-[11px] text-right tabular-nums border outline-none transition-colors ${
              agotada ? 'cursor-not-allowed opacity-60' : ''
            } ${
              excede || invalida
                ? (dark ? 'border-red-500/60 bg-red-500/10 text-red-300'
                        : 'border-red-300 bg-red-50 text-red-700')
                : dark
                  ? 'bg-white/[0.04] border-white/10 text-slate-200 focus:border-sky-500/60'
                  : 'bg-white border-slate-200 text-slate-800 focus:border-sky-400'
            }`}
          />
          {!agotada && cantidad !== linea.disponible && (
            <button
              type="button"
              onClick={() => onCantidad(linea, linea.disponible)}
              title={t('salesOrders.nuevaCarga.maxTooltip', {
                disponible: fmtQty(linea.disponible),
              })}
              className={`px-1.5 py-1 rounded text-[9px] font-bold uppercase tracking-wider transition-colors cursor-pointer ${
                dark ? 'text-slate-500 hover:text-sky-300 hover:bg-white/8'
                     : 'text-slate-400 hover:text-sky-600 hover:bg-slate-100'
              }`}
            >
              {t('salesOrders.nuevaCarga.max')}
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

// ── Confirmación ──────────────────────────────────────────────────────────────

/** Lo que se ve al enviar.
 *
 *  No dice «carga creada» porque no lo está: decirlo mandaría al usuario a buscar
 *  en la lista algo que no va a aparecer hasta dentro de un rato. */
function Confirmacion({
  dark, resultado, onClose,
}: { dark: boolean; resultado: CargaCreateResult; onClose: () => void }) {
  const { t } = useTranslation()
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const dimTxt   = dark ? 'text-slate-600' : 'text-slate-400'

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-4">
      <div className={`p-3 rounded-full ${dark ? 'bg-emerald-500/12' : 'bg-emerald-50'}`}>
        <svg
          className={`w-7 h-7 ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
          strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </div>
      <div>
        <p className={`text-[14px] font-semibold ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
          {t('salesOrders.nuevaCarga.enviadaTitulo')}
        </p>
        <p className={`text-[12px] mt-1.5 max-w-[420px] leading-relaxed ${mutedTxt}`}>
          {t('salesOrders.nuevaCarga.enviadaTexto')}
        </p>
        <p className={`text-[10px] mt-3 font-mono ${dimTxt}`} title={resultado.solicitudId}>
          {resultado.solicitudId.slice(0, 8)}
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 rounded-lg text-[12px] font-semibold bg-sky-600 text-white hover:bg-sky-500 transition-colors cursor-pointer shadow-sm"
      >
        {t('salesOrders.nuevaCarga.volverAlPedido')}
      </button>
    </div>
  )
}

// ── Errores del servidor ──────────────────────────────────────────────────────

interface ErrorServidor {
  codigo: string
  errores: CargaValidacionError[]
  mensaje?: string
}

/** El servidor valida otra vez y puede ver cosas que el cliente no: cargas que
 *  aparecieron entre medias, o una solicitud de otra persona. Sus códigos son los
 *  mismos, así que se traducen igual. */
function leerError(e: unknown): ErrorServidor {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (detail && typeof detail === 'object') {
    const d = detail as { codigo?: string; errores?: CargaValidacionError[]; mensaje?: string }
    return {
      codigo: d.codigo ?? 'desconocido',
      errores: d.errores ?? [],
      mensaje: d.mensaje,
    }
  }
  return {
    codigo: 'desconocido',
    errores: [],
    mensaje: typeof detail === 'string' ? detail : undefined,
  }
}

function AvisoError({
  dark, error, disponibles,
}: { dark: boolean; error: ErrorServidor; disponibles: DisponibleLinea[] }) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const porTransid = new Map(disponibles.map(d => [d.transid, d]))
  const cls = dark
    ? 'bg-red-500/10 border-red-500/25 text-red-300'
    : 'bg-red-50 border-red-200 text-red-700'

  const titulo = error.codigo === 'duplicada'
    ? t('salesOrders.nuevaCarga.errores.duplicada')
    : error.mensaje ?? t(`salesOrders.nuevaCarga.errores.${error.codigo}`, {
        defaultValue: t('salesOrders.nuevaCarga.errores.desconocido'),
      })

  return (
    <div className={`rounded-lg border px-3 py-2.5 text-[11px] leading-relaxed ${cls}`}>
      <p className="font-semibold">{titulo}</p>
      {error.errores.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {error.errores.map((e, i) => {
            const linea = e.inventtransid ? porTransid.get(e.inventtransid) : undefined
            const etiqueta = linea
              ? `${linea.numLinea ?? '—'} · ${fmtArt(linea.articulo)}`
              : e.articulo ?? e.inventtransid ?? ''
            return (
              <li key={i}>
                <span className="font-mono">{etiqueta}</span>
                {' — '}
                {t(`salesOrders.nuevaCarga.errores.${e.codigo}`, {
                  ...(e.valores ?? {}),
                  defaultValue: t('salesOrders.nuevaCarga.errores.desconocido'),
                })}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// ── Sección ───────────────────────────────────────────────────────────────────

function Seccion({
  titulo, nota, acciones, dark, children,
}: {
  titulo: string
  nota?: string
  acciones?: React.ReactNode
  dark: boolean
  children: React.ReactNode
}) {
  const border = dark ? 'border-white/[0.07]' : 'border-slate-200'
  return (
    <section>
      <div className={`flex items-baseline gap-2 flex-wrap pb-2 mb-3 border-b ${border}`}>
        <h3 className={`text-[10px] font-bold uppercase tracking-wider ${
          dark ? 'text-slate-400' : 'text-slate-500'}`}>
          {titulo}
        </h3>
        {nota && (
          <span className={`text-[10px] ${dark ? 'text-slate-600' : 'text-slate-400'}`}>
            {nota}
          </span>
        )}
        {acciones && <span className="ml-auto">{acciones}</span>}
      </div>
      {children}
    </section>
  )
}
